'use client';

/** The two checkbox preferences: chat tabs, and passive memory saving. */

import React from 'react';
import { MAX_CHAT_TABS } from '../../hooks/useChatTabs';
import type { useMemoryPreference } from '../../hooks/useMemoryPreference';

export type MemoryPreference = ReturnType<typeof useMemoryPreference>;

export function ChatTabsSection({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-foreground mb-2">Chat tabs</h3>
      <p className="text-xs text-muted-foreground mb-3">
        Keep several conversations open side by side, the way editor tabs work.
      </p>
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 rounded border-input"
          aria-label="Show a tab strip for open chats"
        />
        <span>
          Show a tab strip for open chats
          <span className="block text-xs text-muted-foreground">
            Open chats stay loaded, so a chat you left working keeps streaming while you use
            another one. Up to {MAX_CHAT_TABS} at a time; the oldest closes after that. Turning
            this off closes the strip — no conversation is lost.
          </span>
        </span>
      </label>
    </section>
  );
}

/**
 * Passive memory saving only. Recall of stored memories and an explicit
 * "remember this" are unaffected by this switch.
 */
export function MemorySection({
  memoryPref,
  saved,
  onChange,
}: {
  memoryPref: MemoryPreference;
  saved: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <section>
      <h3 className="text-sm font-semibold text-foreground mb-2">Memory</h3>
      <p className="text-xs text-muted-foreground mb-3">
        Meridian keeps durable notes about you — how to address you, spellings you use,
        preferences worth honoring — and uses them in every chat.
      </p>
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={memoryPref.autoExtract}
          disabled={memoryPref.isLoading || memoryPref.isSaving}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 rounded border-input"
          aria-label="Let Meridian save new memories on its own"
        />
        <span>
          Let Meridian save new memories on its own
          <span className="block text-xs text-muted-foreground">
            Off means nothing is saved unless you ask. What Meridian already knows still applies,
            and you can always say &ldquo;remember this&rdquo; or &ldquo;forget that.&rdquo;
          </span>
        </span>
      </label>
      {memoryPref.error && <p className="text-sm text-destructive mt-2">{memoryPref.error}</p>}
      {saved && <p className="text-sm text-green-600 dark:text-green-400 mt-2">Saved.</p>}
    </section>
  );
}
